import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { TabKicker, TabSectionTitle } from "./_shell";

/**
 * SalaryBySubject — horizontal bar chart of median 3-year post-grad
 * salaries broken down by subject at one uni. Reads
 * `/universities/{id}/salary-by-subject`. Hides gracefully when the
 * endpoint returns fewer than 2 subjects (not enough signal to chart).
 *
 * Bars are ranked descending. The strongest subject gets a filled
 * accent bar; the rest are tonally muted so the top of the ranking
 * pops without overwhelming.
 */
export default function SalaryBySubject({ uniId, format }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get(`/universities/${uniId}/salary-by-subject?limit=8`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [uniId]);

  if (!data || (data.subjects || []).length < 2) return null;
  const rows = data.subjects;
  const max = Math.max(...rows.map((s) => s.salary_3y_usd));
  const fmt = typeof format === "function"
    ? format
    : (v) => `$${(v || 0).toLocaleString()}`;

  return (
    <section className="mb-8" data-testid="salary-by-subject">
      <div>
        <TabKicker>02 · Salary by Subject</TabKicker>
        <TabSectionTitle>What each subject earns, three years out</TabSectionTitle>
      </div>

      <div className="border border-[#002147]/10 bg-white/40 p-6">
        <div className="space-y-3">
          {rows.map((s, i) => {
            const w = (s.salary_3y_usd / max) * 100;
            const isTop = i === 0;
            return (
              <div
                key={s.subject}
                className="grid grid-cols-[minmax(0,150px)_1fr_100px] items-center gap-4"
                data-testid={`salary-row-${s.subject.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="font-sans text-sm text-[#002147] truncate">
                  {s.subject}
                </span>
                <div className="h-6 bg-[#002147]/[0.04] relative">
                  <div
                    className={`absolute inset-y-0 left-0 flex items-center justify-end pr-2 ${
                      isTop ? "bg-[#7C3AED]" : "bg-[#7C3AED]/40"
                    }`}
                    style={{ width: `${Math.max(2, w)}%` }}
                  />
                </div>
                <span className="font-serif text-base text-[#002147] tabular-nums text-right">
                  {fmt(s.salary_3y_usd)}
                </span>
              </div>
            );
          })}
        </div>
        <p className="mt-4 pt-4 border-t border-[#002147]/10 font-mono text-[10px] uppercase tracking-widest text-[#002147]/50">
          Median 3-year post-graduation salary · {data.total_subjects_with_signal} subjects with signal · HESA LEO / US Scorecard
        </p>
      </div>
    </section>
  );
}
